// pages/Overview.js - Halaman Utama setelah Login
import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, api } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import {
  Cloud, DollarSign, Ticket, Users, Boxes, ScrollText,
  ArrowRight, Activity, CheckCircle2, AlertTriangle, KeyRound, Table2,
  FileText, MonitorDot, Link2, MessageCircle, MessageSquare, UserCog, ShieldCheck, CalendarClock, Sparkles,
  History, LayoutDashboard, Headphones, ChevronDown,
} from 'lucide-react';
import { ROUTE_META } from '../routesMeta';
import { ALL_MENUS } from '../components/Sidebar';
import { GROUP_ORDER, GROUP_KEY, groupOf } from '../config/navGroups';

// ── Card definitions ──────────────────────────────────────────────────────────
// Grup TIDAK lagi ditulis di sini — diambil dari config/navGroups lewat
// groupOf(key), sumber yang sama dengan sidebar dan navigasi mobile.
// gradientStyle: inline agar tidak ter-purge saat build.
const ALL_CARDS = [
  // ── AWS ──────────────────────────────────────────────────────────────────────
  {
    key: 'aws_billing', path: '/dashboard/aws-billing', label: 'AWS Billing',
    descKey: 'overview.card.awsBilling.desc', statKey: 'overview.card.awsBilling.stat',
    icon: Cloud, gradientStyle: 'linear-gradient(to bottom right, #fbbf24, #f97316)',
    bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700',
  },
  {
    key: 'aws_cost', path: '/dashboard/aws-cost', label: 'AWS Cost Explorer',
    descKey: 'overview.card.awsCost.desc', statKey: 'overview.card.awsCost.stat',
    icon: DollarSign, gradientStyle: 'linear-gradient(to bottom right, #fb923c, #ef4444)',
    bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700',
  },
  {
    key: 'aws_inventory', path: '/dashboard/aws-inventory', label: 'AWS Inventory',
    descKey: 'overview.card.awsInventory.desc', statKey: 'overview.card.awsInventory.stat',
    icon: Boxes, gradientStyle: 'linear-gradient(to bottom right, #22d3ee, #0d9488)',
    bg: 'bg-cyan-50', border: 'border-cyan-200', text: 'text-cyan-700',
  },
  {
    key: 'aws_cloudtrail', path: '/dashboard/aws-cloudtrail', label: 'AWS CloudTrail',
    descKey: 'overview.card.awsCloudTrail.desc', statKey: 'overview.card.awsCloudTrail.stat',
    icon: ScrollText, gradientStyle: 'linear-gradient(to bottom right, #fb7185, #e11d48)',
    bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-700',
  },

  // ── SAP ───────────────────────────────────────────────────────────────────────
  {
    key: 'solman', path: '/dashboard/solman', label: 'SAP Solution Manager',
    descKey: 'overview.card.solman.desc', statKey: 'overview.card.solman.stat',
    icon: Ticket, gradientStyle: 'linear-gradient(to bottom right, #60a5fa, #6366f1)',
    bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700',
  },
  {
    key: 'sap_usage', path: '/dashboard/sap-usage', label: 'SAP User Utilization',
    descKey: 'overview.card.sapUsage.desc', statKey: 'overview.card.sapUsage.stat',
    icon: Users, gradientStyle: 'linear-gradient(to bottom right, #34d399, #14b8a6)',
    bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700',
  },
  {
    key: 'sap_user_mgmt', path: '/dashboard/sap-user-maintenance', label: 'SAP User Maintenance',
    descKey: 'overview.card.sapUserMgmt.desc', statKey: 'overview.card.sapUserMgmt.stat',
    icon: KeyRound, gradientStyle: 'linear-gradient(to bottom right, #a78bfa, #7c3aed)',
    bg: 'bg-violet-50', border: 'border-violet-200', text: 'text-violet-700',
  },
  {
    key: 'sap_basis_mon', path: '/dashboard/sap-basis', label: 'SAP Basis Monitor',
    descKey: 'overview.card.sapBasisMon.desc', statKey: 'overview.card.sapBasisMon.stat',
    icon: Activity, gradientStyle: 'linear-gradient(to bottom right, #2dd4bf, #0f766e)',
    bg: 'bg-teal-50', border: 'border-teal-200', text: 'text-teal-700',
  },

  // ── HR ────────────────────────────────────────────────────────────────────────
  {
    key: 'employee_overtime', path: '/dashboard/employee-overtime', label: 'Employee Overtime',
    descKey: 'overview.card.employeeOvertime.desc', statKey: 'overview.card.employeeOvertime.stat',
    icon: CalendarClock, gradientStyle: 'linear-gradient(to bottom right, #fbbf24, #f59e0b)',
    bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700',
  },

  // ── INFRA ─────────────────────────────────────────────────────────────────────
  {
    key: 'sdp', path: '/dashboard/sdp', label: 'ManageEngine SDP Cloud',
    descKey: 'overview.card.sdp.desc', statKey: 'overview.card.sdp.stat',
    icon: Headphones, gradientStyle: 'linear-gradient(to bottom right, #fb923c, #ea580c)',
    bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700',
  },

  // ── TOOLS (semua user login) ──────────────────────────────────────────────────
  {
    key: 'notes', path: '/dashboard/notes', label: 'Notes',
    descKey: 'overview.card.notes.desc', statKey: null,
    icon: FileText, gradientStyle: 'linear-gradient(to bottom right, #fbbf24, #f59e0b)',
    bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700',
  },

  // ── ADMIN ─────────────────────────────────────────────────────────────────────
  {
    key: 'logs', path: '/dashboard/logs', label: 'Log Viewer',
    descKey: 'overview.card.logViewer.desc', statKey: 'overview.card.logViewer.stat',
    icon: FileText, gradientStyle: 'linear-gradient(to bottom right, #fb923c, #ea580c)',
    bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700',
    adminOnly: true,
  },
  {
    key: 'ai_workspace', path: '/dashboard/ai-workspace', label: 'AI Workspace',
    descKey: 'overview.card.aiWorkspace.desc', statKey: 'overview.card.aiWorkspace.stat',
    icon: Sparkles, gradientStyle: 'linear-gradient(to bottom right, #818cf8, #6366f1)',
    bg: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-700',
  },
  {
    key: 'users', path: '/dashboard/users', label: 'User Management',
    descKey: 'overview.card.userMgmt.desc', statKey: 'overview.card.userMgmt.stat',
    icon: UserCog, gradientStyle: 'linear-gradient(to bottom right, #f472b6, #db2777)',
    bg: 'bg-pink-50', border: 'border-pink-200', text: 'text-pink-700',
    adminOnly: true,
  },
  {
    key: 'roles', path: '/dashboard/roles', label: 'Role Management',
    descKey: 'overview.card.roleMgmt.desc', statKey: 'overview.card.roleMgmt.stat',
    icon: ShieldCheck, gradientStyle: 'linear-gradient(to bottom right, #fbbf24, #d97706)',
    bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700',
    adminOnly: true,
  },
  {
    key: 'table_maintenance', path: '/dashboard/table-maintenance', label: 'Table Maintenance',
    descKey: 'overview.card.tableMaintenance.desc', statKey: 'overview.card.tableMaintenance.stat',
    icon: Table2, gradientStyle: 'linear-gradient(to bottom right, #94a3b8, #475569)',
    bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-700',
    adminOnly: true,
  },
  {
    key: 'telegram', path: '/dashboard/telegram', label: 'Telegram Bot',
    descKey: 'overview.card.telegram.desc', statKey: 'overview.card.telegram.stat',
    icon: MessageCircle, gradientStyle: 'linear-gradient(to bottom right, #38bdf8, #0284c7)',
    bg: 'bg-sky-50', border: 'border-sky-200', text: 'text-sky-700',
  },
  {
    key: 'telegram_log', path: '/dashboard/telegram-log', label: 'Telegram Chat Log',
    descKey: 'overview.card.telegramLog.desc', statKey: 'overview.card.telegramLog.stat',
    icon: MessageSquare, gradientStyle: 'linear-gradient(to bottom right, #818cf8, #4f46e5)',
    bg: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-700',
  },
  {
    key: 'share_links', path: '/dashboard/share-links', label: 'Share Links',
    descKey: 'overview.card.shareLinks.desc', statKey: 'overview.card.shareLinks.stat',
    icon: Link2, gradientStyle: 'linear-gradient(to bottom right, #2dd4bf, #0d9488)',
    bg: 'bg-teal-50', border: 'border-teal-200', text: 'text-teal-700',
    adminOnly: true,
  },
  {
    key: 'sessions', path: '/dashboard/sessions', label: 'Session Log',
    descKey: 'overview.card.sessionLog.desc', statKey: 'overview.card.sessionLog.stat',
    icon: MonitorDot, gradientStyle: 'linear-gradient(to bottom right, #34d399, #059669)',
    bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700',
    adminOnly: true,
  },
];

// ── Auto-detect: merge ROUTES dengan ALL_CARDS ────────────────────────────────
// Halaman yang ada di ROUTES tapi belum ada di ALL_CARDS (baru ditambahkan)
// akan otomatis muncul di Overview untuk admin dengan styling default.
// Grup diambil dari config/navGroups — TIDAK lagi ditebak dari prefix
// permission. Tebakan lama memetakan apa pun yang tidak dikenal ke 'ADMIN',
// sehingga AI Coding, AI Actions, Notes Plain, dan File Deploy semuanya
// menumpuk di satu kolom Admin walau sidebar sudah memisahkannya. Halaman
// yang belum terdaftar kini jatuh ke TOOLS (lihat FALLBACK_GROUP), bukan ADMIN.
// Halaman tanpa kartu khusus di ALL_CARDS: ikon, warna, dan deskripsi diambil
// dari menu sidebar (ALL_MENUS) supaya tampil sama dengan sidebar, bukan kartu abu-abu generik.
const MENU_BY_KEY = Object.fromEntries(ALL_MENUS.map(m => [m.key, m]));
const GRADIENT_HEX = {
  slate: ['#94a3b8', '#475569'], gray: ['#9ca3af', '#4b5563'], red: ['#f87171', '#dc2626'],
  orange: ['#fb923c', '#ea580c'], amber: ['#fbbf24', '#d97706'], yellow: ['#facc15', '#ca8a04'],
  lime: ['#a3e635', '#65a30d'], green: ['#4ade80', '#16a34a'], emerald: ['#34d399', '#059669'],
  teal: ['#2dd4bf', '#0d9488'], cyan: ['#22d3ee', '#0891b2'], sky: ['#38bdf8', '#0284c7'],
  blue: ['#60a5fa', '#2563eb'], indigo: ['#818cf8', '#4f46e5'], violet: ['#a78bfa', '#7c3aed'],
  purple: ['#c084fc', '#9333ea'], fuchsia: ['#e879f9', '#c026d3'], pink: ['#f472b6', '#db2777'],
  rose: ['#fb7185', '#e11d48'],
};
function menuVisual(key) {
  const m = MENU_BY_KEY[key];
  if (!m) {
    return {
      icon: LayoutDashboard, gradientStyle: 'linear-gradient(to bottom right, #94a3b8, #64748b)',
      bg: 'bg-slate-50', border: 'border-slate-200', text: 'text-slate-700',
      isNewPage: true, // belum terdaftar di sidebar → badge "New"
    };
  }
  const c = (String(m.color || '').match(/text-([a-z]+)-\d/) || [])[1] || 'slate';
  const [from, to] = GRADIENT_HEX[c] || GRADIENT_HEX.slate;
  return {
    icon: m.icon || LayoutDashboard,
    gradientStyle: `linear-gradient(to bottom right, ${from}, ${to})`,
    bg: m.bgColor || 'bg-slate-50', border: 'border-slate-200', text: m.color || 'text-slate-700',
    subKey: m.subKey || null, descFallback: m.fallbackSub || null,
  };
}
const CARD_MAP = Object.fromEntries(ALL_CARDS.map(c => [c.key, c]));
// Gabungkan: semua ROUTES (kecuali 'overview') jadi card.
// ROUTES yang sudah ada di ALL_CARDS → pakai metadata lama (icon, gradient, dll).
// ROUTES baru yang belum ada → default styling generik, adminOnly: true.
const ENRICHED_CARDS = ROUTE_META
  .filter(r => r.key !== 'overview')
  .map(r => {
    // Grup SELALU dari groupOf() — juga untuk kartu yang metadatanya sudah
    // ada di ALL_CARDS, supaya tidak ada dua kebenaran soal grup.
    if (CARD_MAP[r.key]) return { ...CARD_MAP[r.key], group: groupOf(r.key), permission: r.permission };
    // Halaman baru — default styling
    return {
      key:           r.key,
      group:         groupOf(r.key),
      path:          r.path,
      label:         r.label,
      permission:    r.permission,
      descKey:       null,
      statKey:       null,
      ...menuVisual(r.key),
      adminOnly:     r.permission === '__admin__',  // hanya '__admin__' yang admin-only; permission biasa → tampil sesuai hasAccess
    };
  });

// ── Group display config ─────────────────────────────────────────────────────
// Diturunkan dari GROUP_ORDER supaya urutan kolom di Overview persis mengikuti
// urutan grup di sidebar. Grup tanpa kartu terlihat tersaring sendiri di bawah,
// jadi daftar ini boleh memuat semua grup.
const GROUPS = GROUP_ORDER.map(key => ({ key, labelKey: GROUP_KEY[key], fallback: key }));

// Grup yang dilipat di halaman Overview, disimpan sebagai array key di
// localStorage — pola & key SENGAJA beda dari sidebar (yang menyimpan di
// 'sb_closed_groups') karena keduanya adalah UI independen; pengguna bisa
// melipat 'AWS' di Overview tanpa ikut melipatnya di sidebar. Yang disimpan
// adalah grup TERTUTUP, bukan yang terbuka, supaya grup baru yang ditambahkan
// nanti tampil terbuka secara default.
const OVERVIEW_CLOSED_GROUPS_KEY = 'ov_closed_groups';

const ROLE_STYLES = {
  admin: 'bg-blue-100 text-blue-700',
  SAP:   'bg-emerald-100 text-emerald-700',
  AWS:   'bg-amber-100 text-amber-700',
};

// ── Lightweight stats per card ────────────────────────────────────────────────
async function loadCardStat(key) {
  switch (key) {
    case 'aws_billing': {
      const { data } = await api.get('/api/billing/status');
      return data?.periods?.length ?? 0;
    }
    case 'aws_cost': {
      const { data } = await api.get('/api/aws/years');
      return data?.years?.length ?? 0;
    }
    case 'solman': {
      const { data } = await api.get('/api/tickets/summary');
      return data?.overall?.open ?? 0;
    }
    case 'sap_usage': {
      const { data } = await api.get('/api/sap-usage/status');
      return data?.count ?? 0;
    }
    case 'aws_inventory': {
      const { data } = await api.get('/api/aws-inventory/status');
      return data?.totalResources ?? 0;
    }
    case 'aws_cloudtrail': {
      const { data } = await api.get('/api/aws-cloudtrail/status');
      return data?.totalEvents ?? 0;
    }
    case 'sap_user_mgmt': {
      const { data } = await api.get('/api/sap/connections');
      return (data || []).filter(p => p.pathReadtable && p.hasPassword).length;
    }
    case 'sap_basis_mon': {
      const { data } = await api.get('/api/sap/connections');
      return (Array.isArray(data) ? data : (data?.connections || data?.rows || [])).length;
    }
    case 'sdp': {
      const { data } = await api.get('/api/sdp/summary');
      return data?.openRequests ?? 0;
    }
    default:
      return null;
  }
}

export default function Overview() {
  const { user, hasAccess } = useAuth();
  const { t } = useLang();

  // Filter akses — meniru logika Sidebar:
  //   admin → semua; selain itu: bukan adminOnly & (permission null = semua user,
  //   atau permission ada di daftar permission user). permission '__admin__' → admin saja.
  const visibleCards = ENRICHED_CARDS.filter(c => {
    if (user?.role === 'admin') return true;
    if (c.adminOnly || c.permission === '__admin__') return false;
    if (!c.permission) return true;            // permission null → semua user login
    return hasAccess(c.permission);
  });

  const [stats,  setStats]  = useState({});
  const [online, setOnline] = useState(null);
  const [allOk,  setAllOk]  = useState(true);
  const [recent, setRecent] = useState(null); // null = loading, [] = none
  const [closedGroups, setClosedGroups] = useState(() => {
    try {
      const raw = localStorage.getItem(OVERVIEW_CLOSED_GROUPS_KEY);
      return new Set(raw ? JSON.parse(raw) : []);
    } catch { return new Set(); }
  });

  useEffect(() => {
    try { localStorage.setItem(OVERVIEW_CLOSED_GROUPS_KEY, JSON.stringify([...closedGroups])); }
    catch { /* private mode: lipatan jadi sesi-saja, bukan galat */ }
  }, [closedGroups]);

  const toggleGroup = (key) => {
    setClosedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let healthOk = true;
      try { await api.get('/api/health'); } catch { healthOk = false; }
      if (cancelled) return;
      setOnline(healthOk);

      const results = await Promise.allSettled(visibleCards.map(c => loadCardStat(c.key)));
      if (cancelled) return;
      const next = {};
      let ok = healthOk;
      results.forEach((r, i) => {
        const key = visibleCards[i].key;
        if (r.status === 'fulfilled') next[key] = r.value;
        else { next[key] = '—'; ok = false; }
      });
      setStats(next);
      setAllOk(ok);
    })();
    return () => { cancelled = true; };
  }, [user?.role]); // eslint-disable-line

  // ── Most recent visits — derived from VIEW_DASHBOARD activity log ────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Endpoint membatasi non-admin ke username sendiri; admin difilter via param.
        const { data } = await api.get('/api/logs', {
          params: { action: 'VIEW_DASHBOARD', username: user?.username, limit: 40 },
        });
        if (cancelled) return;
        const logs = data?.logs || [];
        // detail = "Membuka: <label>" → ambil label, map ke card, ambil 4 unik terbaru.
        const seen = new Set();
        const out  = [];
        for (const log of logs) {
          const label = String(log.detail || '').replace(/^Membuka:?\s*/i, '').trim();
          if (!label) continue;
          const card = visibleCards.find(c => c.label.toLowerCase() === label.toLowerCase());
          if (!card || seen.has(card.key)) continue;
          seen.add(card.key);
          out.push({ ...card, at: log.created_at });
          if (out.length >= 4) break;
        }
        setRecent(out);
      } catch {
        if (!cancelled) setRecent([]);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.username]); // eslint-disable-line

  // Build group → cards map (only visible groups, only when not searching)
  const groupedCards = useMemo(() =>
    GROUPS.map(g => ({
      ...g,
      cards: visibleCards.filter(c => c.group === g.key),
    })).filter(g => g.cards.length > 0),
  [visibleCards]); // eslint-disable-line

  // Shared card renderer
  const renderCard = (card, { showGroupBadge = false } = {}) => {
    const Icon = card.icon;
    const groupMeta = GROUPS.find(g => g.key === card.group);
    const groupLabel = groupMeta
      ? (t(groupMeta.labelKey) !== groupMeta.labelKey ? t(groupMeta.labelKey) : groupMeta.fallback)
      : card.group;
    return (
      <Link
        key={card.key}
        to={card.path}
        className="group relative rounded-xl bg-white border border-slate-200/70 px-4 py-3.5 max-sm:!p-3 shadow-sm hover:border-slate-300 hover:shadow-lg hover:shadow-slate-200/70 hover:-translate-y-0.5 transition-all duration-200 flex flex-col"
      >
        <div className="flex items-center gap-3 max-sm:gap-2.5">
          <div className={`w-9 h-9 max-sm:w-8 max-sm:h-8 rounded-xl flex items-center justify-center flex-shrink-0 ring-1 ring-inset ring-black/5 ${card.bg}`}>
            <Icon className={`${card.text} w-[18px] h-[18px] max-sm:w-4 max-sm:h-4`} strokeWidth={1.75} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="font-display font-semibold text-slate-800 text-sm max-sm:text-[13px] leading-tight group-hover:text-brand-600 transition-colors max-sm:line-clamp-2 max-sm:min-h-[2.5rem] max-sm:flex max-sm:items-center">
                {card.label}
              </h4>
              {showGroupBadge && (
                <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-400 leading-none">
                  {groupLabel}
                </span>
              )}
              {card.isNewPage && (
                <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-brand-100 text-brand-600 leading-none">
                  New
                </span>
              )}
            </div>
            <p className="text-xs max-sm:hidden text-slate-500 mt-0.5 leading-snug sm:line-clamp-1">
              {card.descKey ? t(card.descKey) : (card.subKey && t(card.subKey) !== card.subKey ? t(card.subKey) : (card.descFallback || card.label))}
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between mt-3 max-sm:mt-2 min-h-[1rem]">
          <div className={card.statKey ? `text-[11px] whitespace-nowrap truncate px-2 py-0.5 rounded-full ${card.bg} ${card.text}` : ''}>
            {card.statKey && (
              <>
                <span className="font-semibold tabular-nums">
                  {stats[card.key] === undefined ? '…' : stats[card.key]}
                </span>{' '}
                {t(card.statKey)}
              </>
            )}
          </div>
          <ArrowRight className="text-slate-300 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 group-hover:text-brand-600 transition-all duration-200 flex-shrink-0" style={{ width: 14, height: 14 }} />
        </div>
      </Link>
    );
  };

  return (
    <div
      className="px-7 py-6 max-sm:p-4 content-animate"
      style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 6rem)' }}
    >
      {/* Welcome header */}
      <div className="relative hidden sm:flex items-center justify-between gap-6 rounded-2xl border border-brand-300/70 bg-gradient-to-r from-brand-200 via-brand-100 to-brand-50 px-7 py-5 mb-6 overflow-hidden shadow-sm shadow-brand-900/5">
        <span aria-hidden className="pointer-events-none absolute -top-20 -left-10 w-80 h-48 rounded-full bg-brand-300/25 blur-3xl" />
        <span aria-hidden className="pointer-events-none absolute -bottom-24 right-1/3 w-64 h-40 rounded-full bg-brand-200/30 blur-3xl" />
        <div className="relative min-w-0">
          <h2 className="font-display text-xl font-semibold tracking-tight text-slate-800 truncate">
            {t('overview.welcome', { name: user?.username })}
          </h2>
          <p className="text-slate-500 text-sm mt-1 truncate">
            {t('overview.accessInfo', { count: visibleCards.length })}
          </p>
        </div>
        <div className="relative flex-shrink-0 flex items-center gap-6">
          {online === false ? (
            <span className="inline-flex items-center gap-1.5 text-red-600 text-xs font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500" />{t('overview.systemOffline')}
            </span>
          ) : allOk ? (
            <span className="inline-flex items-center gap-1.5 text-slate-500 text-xs font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />{t('overview.allNormal')}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-amber-600 text-xs font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />{t('overview.someUnavailable')}
            </span>
          )}
          <span className="w-px h-10 bg-brand-200" />
          <div className="text-right">
            <div className="text-2xl font-semibold text-brand-700 leading-none tabular-nums">{visibleCards.length}</div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-brand-700/60 mt-1.5">{t('overview.statDashboards')}</div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-semibold text-brand-600 leading-none tabular-nums">{groupedCards.length}</div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-brand-700/60 mt-1.5">{t('overview.statCategories')}</div>
          </div>
        </div>
      </div>

      {/* Most recent visits — only when not actively searching */}
      {recent && recent.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <History className="text-slate-400" style={{ width: 15, height: 15 }} />
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
              {t('overview.recent.title') !== 'overview.recent.title' ? t('overview.recent.title') : 'Recently Visited'}
            </span>
          </div>
          <div className="flex flex-nowrap sm:flex-wrap gap-2 overflow-x-auto sm:overflow-visible -mx-1 px-1 pb-1" style={{ scrollbarWidth: 'none' }}>
            {recent.map(card => {
              const Icon = card.icon;
              return (
                <Link
                  key={card.key}
                  to={card.path}
                  className="group flex items-center gap-2 pl-1.5 pr-3 py-1.5 rounded-full border border-slate-200/70 bg-white hover:border-slate-300 transition-all flex-shrink-0"
                >
                  <span
                    className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${card.bg}`}
                  >
                    <Icon className={card.text} style={{ width: 13, height: 13 }} />
                  </span>
                  <span className="text-xs font-medium text-slate-700 group-hover:text-brand-600 transition-colors whitespace-nowrap">
                    {card.label}
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Grouped layout ── */}
      {(
        <div className="space-y-9 max-sm:space-y-6">
          {groupedCards.map(group => {
            const isClosed = closedGroups.has(group.key);
            return (
            <section key={group.key}>
              <button
                type="button"
                onClick={() => toggleGroup(group.key)}
                aria-expanded={!isClosed}
                className="w-full flex items-center gap-3 mb-3 group/hdr text-left"
              >
                <ChevronDown
                  style={{ width: 14, height: 14, transition: 'transform .15s ease',
                    transform: isClosed ? 'rotate(-90deg)' : 'none' }}
                  className="flex-shrink-0 text-slate-400 group-hover/hdr:text-slate-600"
                />
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-400 sm:text-slate-500 sm:tracking-[0.12em] group-hover/hdr:text-slate-700">
                  {t(group.labelKey) !== group.labelKey ? t(group.labelKey) : group.fallback}
                </span>
                <span className="hidden sm:inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-slate-100 text-slate-500 text-[10px] font-semibold leading-none">
                  {group.cards.length}
                </span>
                <div className="flex-1 h-px bg-slate-100 sm:bg-slate-200" />
                <span className="text-xs text-slate-300 font-medium sm:hidden">{group.cards.length}</span>
              </button>
              {!isClosed && (
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3 max-sm:gap-2.5">
                  {group.cards.map(card => renderCard(card))}
                </div>
              )}
            </section>
            );
          })}
        </div>
      )}

      {/* Footer Note */}
      <p className="text-xs text-slate-400 text-center mt-8 font-medium">
        {t('overview.footer')}
      </p>
    </div>
  );
}
